import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';
import { BiqButtonComponent } from '../biq-button/biq-button.component';
import { 
  AbstractControl, 
  FormControl, 
  FormsModule, 
  ReactiveFormsModule, 
  ValidationErrors, 
  Validators 
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { AsyncPipe } from '@angular/common';
import { LocationOption, UserBiqData } from '@app/schemas/biq';
import { 
  Observable, 
  debounceTime, 
  distinctUntilChanged, 
  filter, 
  map, 
  of, 
  startWith, 
  switchMap 
} from 'rxjs';
import { BIQ_TOTAL_QUESTIONS, US_LOCATIONS } from '@app/utils/constants';
import { Store } from '@ngrx/store';
import { updateUserBiq } from '@app/state/actions/biq.action';
import { GooglePlacesService } from '@app/services/google/places/google-places.service';

@Component({
  selector: 'app-biq-desired-location',
  imports: [
    ProgressBarComponent,
    BiqButtonComponent,
    FormsModule, 
    MatFormFieldModule, 
    MatInputModule, 
    ReactiveFormsModule,
    MatAutocompleteModule,
    AsyncPipe,
  ],
  templateUrl: './desired-location.component.html',
  styleUrl: './desired-location.component.css'
})
export class DesiredLocationComponent implements OnInit {
  @Output() continueEmitter = new EventEmitter<void>();
  public questNo: number = 6;
  public totalQuestions: number = BIQ_TOTAL_QUESTIONS;
  public locationControl = new FormControl('', [
    Validators.required,
  ]);
  public filteredLocations!: Observable<any[]>;
  public locations: LocationOption[] = US_LOCATIONS;
  public storedBiqData!: UserBiqData;
  public selectedLocation: string | null = null;
  public selectedPlaceId: string | null = null;
  public selectedAdministrativeAreaLevel2: string | null = null;
  public availableOptions: string[] = [];
  public isLoading: boolean = false;
  public isFetchingLocations: boolean = false;

  constructor(
    private store: Store<{ userBiq: UserBiqData }>,
    private googlePlaces: GooglePlacesService
  ) {
    this.store.select('userBiq').subscribe(value => {
      this.storedBiqData = value;
      if (value && value.futureLocation) {
        this.locationControl.setValue(value.futureLocation);
        this.selectedLocation = value.futureLocation;
      }
    });
  }

  ngOnInit() {
    this.filteredLocations = this.locationControl.valueChanges.pipe(
      startWith(''),
      filter((value): value is string => typeof value === 'string'),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value: string) => {
        if (value.length > 2) {
          this.isFetchingLocations = true;
          return this.googlePlaces.getPlacePredictionsWithDetails(value).pipe(
            map((results) => {
              this.isFetchingLocations = false;
              this.availableOptions = results.map(r => r.description);
              return results;
            })
          );
        } else {
          this.isFetchingLocations = false;
          this.availableOptions = [];
          return of([]);
        }
      })
    );

    this.locationControl.setValidators([
      Validators.required,
      this.validateLocation.bind(this)
    ]);

    this.locationControl.updateValueAndValidity();
  }

  onLocationSelected(event: any) {
    const value = event.option.value;
    this.selectedLocation = value.description || value;
    this.selectedPlaceId = value.placeId || null;
    
    // Update form control with the string description, not the object
    this.locationControl.setValue(this.selectedLocation, { emitEvent: false });
    
    // Fetch and store administrative_area_level_2 when location is selected
    if (this.selectedPlaceId) {
      this.googlePlaces.getPlaceDetails(this.selectedPlaceId).subscribe(place => {
        const county = this.googlePlaces.extractAdministrativeAreaLevel2(place);
        console.log('administrative_area_level_2:', county);
        this.selectedAdministrativeAreaLevel2 = county;
      });
    }
    
    this.locationControl.setErrors(null);
    this.locationControl.updateValueAndValidity();
  }

  validateLocation = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;
  
    // If selectedLocation exists and matches, it's valid
    if (this.selectedLocation === value) {
      return null;
    }
  
    // Otherwise, check if value is one of the options
    if (this.availableOptions.includes(value)) {
      return null;
    }
  
    return { invalidLocation: true };
  };

  onClickContinue() {
    this.locationControl.markAsTouched();
    if (this.locationControl.invalid) return;

    this.isLoading = true;
    const updatedBiq = {
      ...this.storedBiqData,
      futureLocation: this.locationControl.value!,
      futureLocationGooglePlaceId: this.selectedPlaceId || undefined,
      futureLocationAdministrativeAreaLevel2: this.selectedAdministrativeAreaLevel2 || undefined,
    }
    this.store.dispatch(updateUserBiq({ user: updatedBiq }));
    this.continueEmitter.emit();
  }
}
